package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.AppPreferences
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * Persisted snapshot of the Add-Business wizard's TEXT draft (QA data-loss fix: "Save draft" used to
 * just exit and the in-memory draft died with the ViewModel). Picked images are process-local file
 * refs that can't survive a restart, so they're intentionally NOT part of the snapshot — the owner
 * re-picks photos when resuming. Empty snapshot ≙ no draft (the store clears instead of saving it).
 */
@Serializable
data class BusinessDraftSnapshot(
    val name: LocalizedText = LocalizedText(),
    val categoryId: String? = null,
    val description: LocalizedText = LocalizedText(),
    val attributes: List<String> = emptyList(),
    val addressLine: String = "",
    val city: String = "",
    val province: String = "",
    val postalCode: String = "",
    val products: List<DraftProductSnapshot> = emptyList(),
) {
    /** True when every field is at its default — nothing worth keeping. */
    val isEmpty: Boolean
        get() = name.isEmpty && categoryId == null && description.isEmpty && attributes.isEmpty() &&
            addressLine.isBlank() && city.isBlank() && province.isBlank() && postalCode.isBlank() &&
            products.isEmpty()
}

@Serializable
data class DraftProductSnapshot(
    val name: String,
    val priceCents: Long,
    val promoted: Boolean = false,
)

/**
 * Persists one [BusinessDraftSnapshot] via [AppPreferences] (JSON). Corrupt/legacy payloads decode
 * to `null` (never crash the wizard over a stale draft). One draft at a time — matching the wizard,
 * which is a single flow.
 */
class BusinessDraftStore(private val preferences: AppPreferences) {
    private val json = Json { ignoreUnknownKeys = true }

    fun load(): BusinessDraftSnapshot? =
        preferences.businessDraftJson?.let { raw ->
            runCatching { json.decodeFromString<BusinessDraftSnapshot>(raw) }.getOrNull()
        }?.takeIf { !it.isEmpty }

    fun save(snapshot: BusinessDraftSnapshot) {
        if (snapshot.isEmpty) {
            clear()
        } else {
            preferences.businessDraftJson = json.encodeToString(BusinessDraftSnapshot.serializer(), snapshot)
        }
    }

    fun clear() {
        preferences.businessDraftJson = null
    }
}
